import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import {
    Bed,
    Bath,
    Car,
    Maximize2,
    CheckCircle2,
    Map,
  } from "lucide-vue-next";
  import CustomerNavbar from "@/v2/components/CustomerNavbar.vue";
  import PageBackButton from "@/v2/components/ui/PageBackButton.vue";
  import { formatPrice } from "@/helpers/transformation/price";
  import builderApi from "@/services/api/builderApi";
  import projectPageApi, {
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import colorSchemeApi from "@/services/api/colorSchemeApi";
  import optionalUpgradeApi from "@/services/api/optionalUpgradeApi";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { LotBaseInterface } from "@/types/LotInterface";
  import { ColorSchemeInterface } from "@/types/ColorSchemeInterface";
  import {
    OptionalUpgrade,
    EstateOptionalUpgrade,
  } from "@/types/OptionalUpgradeInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'SubmitExpressionOfInterestView',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const router = useRouter();

  const house = ref<HouseDesignInterface | null>(null);
  const projectPage = ref<ProjectPageData | null>(null);
  const lots = ref<LotBaseInterface[]>([]);
  const estateColorSchemes = ref<ColorSchemeInterface[]>([]);
  const allUpgrades = ref<(OptionalUpgrade | EstateOptionalUpgrade)[]>([]);

  const formData = ref({
    fullName: "",
    email: "",
    phone: "",
    timeframe: "3-6",
    currentLiving: "own-home",
    notes: "",
    agreeToContact: false,
    agreeToPrivacy: false,
  });

  const houseId = computed(() => route.query.houseId as string);
  const estateId = computed(() => route.query.estateId as string);
  const facadeId = computed(() => route.query.facadeId as string);
  const colorSchemeId = computed(() => route.query.colorSchemeId as string);
  const lotId = computed(() => route.query.lotId as string);
  const isEmbedded = computed(() => route.query.embed === "true");

  const upgradeIds = computed(() => {
    const raw = route.query.upgradeIds as string;

    return raw ? raw.split(",").filter(Boolean) : [];
  });

  const fetchHouse = async () => {
    if (!houseId.value) return;

    const response = await builderApi.getHouseById(houseId.value);

    house.value = response.data;
  };

  const fetchProjectPage = async () => {
    if (!estateId.value) return;

    try {
      const { data } = await projectPageApi.getByEstateIdPublic(estateId.value);

      projectPage.value = data;
    } catch (err) {
      // Project page not found - using defaults
    }
  };

  const fetchLots = async () => {
    if (!estateId.value || !houseId.value) return;

    const response = await builderApi.getLotsByEstateAndHouseDesign(
      estateId.value,
      houseId.value
    );

    lots.value = response.data || [];
  };

  const fetchEstateColorSchemes = async () => {
    if (!estateId.value) return;

    try {
      estateColorSchemes.value = await colorSchemeApi.getColorSchemesByEstateId(
        estateId.value
      );
    } catch {
      estateColorSchemes.value = [];
    }
  };

  const fetchUpgrades = async () => {
    if (!estateId.value && !houseId.value) return;

    const houseUpgrades: OptionalUpgrade[] = houseId.value
      ? await builderApi
          .getHouseById(houseId.value)
          .then((res) => res.data?.optionalUpgrades ?? [])
          .catch(() => [])
      : [];

    const estateUpgrades: EstateOptionalUpgrade[] = estateId.value
      ? await optionalUpgradeApi
          .getOptionalUpgradesByEstateId(estateId.value)
          .catch(() => [])
      : [];

    allUpgrades.value = [...houseUpgrades, ...estateUpgrades];
  };

  onMounted(async () => {
    await Promise.all([
      fetchHouse(),
      fetchProjectPage(),
      fetchLots(),
      fetchEstateColorSchemes(),
      fetchUpgrades(),
    ]);
  });

  const selectedFacade = computed(() => {
    if (!house.value?.facades || !facadeId.value) return null;

    return house.value.facades.find((f) => f._id === facadeId.value) || null;
  });

  const allColorSchemes = computed(() => {
    if (house.value?.internalColorSchemes?.length)
      return house.value.internalColorSchemes as ColorSchemeInterface[];

    return estateColorSchemes.value;
  });

  const selectedColorScheme = computed(() => {
    if (!colorSchemeId.value) return null;

    return (
      allColorSchemes.value.find(
        (s: ColorSchemeInterface) => s._id === colorSchemeId.value
      ) || null
    );
  });

  const selectedLot = computed(() => {
    if (!lots.value.length || !lotId.value) return null;

    return lots.value.find((l) => l._id === lotId.value) || null;
  });

  const selectedUpgrades = computed(() => {
    if (!upgradeIds.value.length) return [];

    return allUpgrades.value.filter(
      (u: OptionalUpgrade | EstateOptionalUpgrade) =>
        upgradeIds.value.includes(u._id)
    );
  });

  const upgradesTotal = computed(() =>
    selectedUpgrades.value.reduce(
      (sum: number, u: OptionalUpgrade | EstateOptionalUpgrade) =>
        sum + (u.price || 0),
      0
    )
  );

  const houseImage = computed(() => {
    return selectedFacade.value?.url || house.value?.mainImages?.[0] || null;
  });

  const bathroomCount = computed(() => {
    if (!house.value) return null;

    return house.value.bathroom?.quantity ?? house.value.bathroom ?? null;
  });

  const hasSpecs = computed(() => {
    return (
      house.value &&
      (house.value.bedroom ||
        bathroomCount.value ||
        house.value.garage ||
        house.value.area)
    );
  });

  const displayPrice = computed(() => {
    if (!house.value) return "$0";

    const selectedHouse = house.value as HouseDesignInterface & {
      price?: number;
    };
    const total =
      (selectedHouse.price || selectedHouse.basePrice || 0) +
      upgradesTotal.value;

    return formatPrice(total);
  });

  const submitted = ref(false);
  const errors = ref<Record<string, string>>({});

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.value.fullName.trim())
      newErrors.fullName = "Full name is required.";

    if (!formData.value.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.value.phone.trim())
      newErrors.phone = "Phone number is required.";

    if (!formData.value.agreeToContact || !formData.value.agreeToPrivacy) {
      newErrors.agreements =
        "Please agree to both statements before submitting.";
    }

    errors.value = newErrors;

    if (Object.keys(newErrors).length > 0) return;

    submitted.value = true;
  };

  const browseMoreDesigns = () => {
    router.push({
      name: "BrowseHomeDesignsV2",
      params: { estateId: estateId.value },
      query: isEmbedded.value ? { embed: "true" } : {},
    });
  };

  const exploreMap = () => {
    router.push({
      name: "MasterplanViewV2",
      params: { estateId: estateId.value },
      query: isEmbedded.value ? { embed: "true" } : {},
    });
  };

  const goBack = () => {
    router.back();
  };

const __returned__ = { route, router, house, projectPage, lots, estateColorSchemes, allUpgrades, formData, houseId, estateId, facadeId, colorSchemeId, lotId, isEmbedded, upgradeIds, fetchHouse, fetchProjectPage, fetchLots, fetchEstateColorSchemes, fetchUpgrades, selectedFacade, allColorSchemes, selectedColorScheme, selectedLot, selectedUpgrades, upgradesTotal, houseImage, bathroomCount, hasSpecs, displayPrice, submitted, errors, handleSubmit, browseMoreDesigns, exploreMap, goBack, ref, computed, onMounted, get useRoute() { return useRoute }, get useRouter() { return useRouter }, get Bed() { return Bed }, get Bath() { return Bath }, get Car() { return Car }, get Maximize2() { return Maximize2 }, get CheckCircle2() { return CheckCircle2 }, get Map() { return Map }, CustomerNavbar, PageBackButton, get formatPrice() { return formatPrice }, get builderApi() { return builderApi }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, get colorSchemeApi() { return colorSchemeApi }, get optionalUpgradeApi() { return optionalUpgradeApi }, get HouseDesignInterface() { return HouseDesignInterface }, get LotBaseInterface() { return LotBaseInterface }, get ColorSchemeInterface() { return ColorSchemeInterface }, get OptionalUpgrade() { return OptionalUpgrade }, get EstateOptionalUpgrade() { return EstateOptionalUpgrade } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})